import { ContextMenu } from "@/components/ContextMenu";
import { useModals } from "@/features/modals/hooks";
import { useDndContext, useDraggable } from "@dnd-kit/core";
import { CSS, type Transform } from "@dnd-kit/utilities";
import styled from "@emotion/styled";
import {
  LightMode,
  Variables,
  documentAddIcon,
  trashCanIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { type ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useCalendarContext } from "../CalendarProvider";
import { TooltipContent } from "../team-calendar/TooltipContent";
import { type CoWorkerCalendarItem } from "../types";
import { allowCopyPaste, allowDragAndDrop, allowResize } from "../utils";
import { AppointmentBlockContainer } from "./AppointmentBlockContainer";
import { DragContainer, ResizeContainer } from "./components";

interface Props {
  id: string;
  children: ReactNode;
  onClick: () => void;
  item: CoWorkerCalendarItem;

  top: number;
  left: number;
  width: number;
  height?: number;
  zIndex?: number;

  verticalMode?: boolean;
}

const TooltipContainer = styled.div<{
  top: number;
  left: number;
}>`
  position: fixed;
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;
  z-index: 9999;
  background: ${LightMode.colourElevation1};
  border: ${Variables.thicknessThin} solid ${LightMode.colourElevation1Border};
  border-radius: ${Variables.radiusM};
  box-shadow: ${Variables.shadowSizeDefault};
  padding: ${Variables.space50} ${Variables.space75};
`;

const TOOLTIP_VIEWPORT_MARGIN = 8;

const getResizeStyle = ({
  verticalMode,
  isDragEnabled,
  resizeIsDragging,
  resizeTransform,
  item,
  width = 0,
  height,
}: {
  verticalMode?: boolean;
  isDragEnabled?: boolean;
  resizeIsDragging: boolean;
  resizeTransform: Transform | null;
  item: CoWorkerCalendarItem;
  width?: number;
  height?: number;
}) => {
  if (verticalMode) {
    return isDragEnabled && resizeIsDragging && resizeTransform
      ? {
          height: item.allDay ? height : (height ?? 0) + resizeTransform.y,
          width: item.allDay ? width + resizeTransform.x : width,
        }
      : undefined;
  } else {
    return isDragEnabled && resizeIsDragging && resizeTransform
      ? {
          width: item.allDay ? width : width + resizeTransform.x,
        }
      : undefined;
  }
};

const getDragStyle = ({
  verticalMode,
  isDragEnabled,
  dragIsDragging,
  dragTransform,
  item,
}: {
  verticalMode?: boolean;
  isDragEnabled?: boolean;
  dragIsDragging: boolean;
  dragTransform: Transform | null;
  item: CoWorkerCalendarItem;
}) => {
  if (verticalMode) {
    return isDragEnabled && dragIsDragging && dragTransform
      ? {
          transform: CSS.Translate.toString({
            ...dragTransform,
            // All day blocks can only be moved to another day
            y: item.allDay ? 0 : dragTransform.y,
          }),
        }
      : undefined;
  } else {
    return isDragEnabled && dragIsDragging && dragTransform
      ? {
          transform: CSS.Translate.toString({
            ...dragTransform,
            // All day blocks can only be moved to another person
            x: item.allDay ? 0 : dragTransform.x,
          }),
        }
      : undefined;
  }
};

export const DraggableAppointmentBlockContainer = ({
  id,
  children,
  item,
  top,
  left,
  width,
  height,
  zIndex,
  onClick,
  verticalMode,
}: Props) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const { copyTask, deleteTask, deleteSchedule, appointmentFilters } =
    useCalendarContext();
  const { calendar } = useSettings();

  const [hover, setHover] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);
  const isDragEnabled = allowDragAndDrop(item, appointmentFilters.tableView);
  const isCopyPasteEnabled = allowCopyPaste(item, appointmentFilters.tableView);
  const isResizeEnabled = allowResize({ ...item, resize: true });

  const {
    attributes: ariaAttributes,
    listeners: dragListeners,
    setNodeRef: dragSetNodeRef,
    transform: dragTransform,
    isDragging: dragIsDragging,
  } = useDraggable({
    id,
    data: { ...item, resize: false },
  });

  const { active } = useDndContext();
  const isGlobalDragging = Boolean(active);

  useEffect(() => {
    if (isGlobalDragging) {
      setHover(false);
    }
  }, [isGlobalDragging]);

  const {
    listeners: resizeListeners,
    setNodeRef: resizeSetNodeRef,
    transform: resizeTransform,
    isDragging: resizeIsDragging,
  } = useDraggable({
    id: `${id}Resize`,
    data: { ...item, resize: true },
  });

  const resizeStyle = getResizeStyle({
    verticalMode,
    isDragEnabled: isResizeEnabled,
    resizeIsDragging,
    resizeTransform,
    item,
    height,
    width,
  });

  const dragStyle = getDragStyle({
    verticalMode,
    isDragEnabled,
    dragIsDragging,
    dragTransform,
    item,
  });

  const menuItems: Parameters<typeof ContextMenu>[0]["menuItems"] = [];

  if (isDragEnabled) {
    if (isCopyPasteEnabled) {
      menuItems.push({
        icon: documentAddIcon,
        label: t("action.copy_event"),
        onClick: () => copyTask(item),
      });
    }

    menuItems.push({
      icon: trashCanIcon,
      label: t("action.delete_event"),
      disabled:
        item.appointmentType === "scheduleActivity" &&
        !calendar?.delete_schedule_entries,
      onClick: () => {
        if (item.appointmentType === "task") {
          pushModal({
            type: "CONFIRM",
            onConfirm: () => deleteTask(Number(item.id)),
            title: t("action.delete_task"),
            message: t("prompt.confirm_delete_task"),
          });
        } else if (item.appointmentType === "scheduleActivity") {
          pushModal({
            type: "CONFIRM",
            onConfirm: () => deleteSchedule(String(item.id)),
            title: t("action.delete_schedule"),
            message: t("prompt.confirm_delete_schedule"),
          });
        }
      },
    });
  }

  useLayoutEffect(() => {
    if (hover && menuRef && tooltipRef) {
      const blockRect = menuRef.getBoundingClientRect();
      const tooltipRect = tooltipRef.getBoundingClientRect();
      const viewportMargin = TOOLTIP_VIEWPORT_MARGIN;
      const maxLeft = window.innerWidth - tooltipRect.width - viewportMargin;
      const preferredTop = blockRect.bottom + 10;
      const top =
        preferredTop + tooltipRect.height <= window.innerHeight - viewportMargin
          ? preferredTop
          : blockRect.top - tooltipRect.height - 10;

      setCoords({
        top: Math.max(viewportMargin, top),
        left: Math.min(
          Math.max(
            viewportMargin,
            blockRect.left + blockRect.width / 2 - tooltipRect.width / 2
          ),
          Math.max(viewportMargin, maxLeft)
        ),
      });
    }
  }, [hover, menuRef, tooltipRef]);

  useEffect(() => {
    dragSetNodeRef(menuRef);
  }, [dragSetNodeRef, menuRef]);

  const shouldShowTooltip = hover && !isGlobalDragging;
  const tooltipId = `tooltip-${id}`;

  return (
    <>
      <ContextMenu
        elRef={menuRef}
        isOpen={isMenuOpen}
        setIsOpen={setIsMenuOpen}
        menuItems={menuItems}
      />
      <AppointmentBlockContainer
        className="saleslead__element--focus-visible"
        ref={setMenuRef}
        top={top}
        left={left}
        width={width}
        height={height}
        zIndex={zIndex}
        style={dragStyle ?? resizeStyle ?? {}}
        {...ariaAttributes}
        {...(isDragEnabled ? dragListeners : {})}
        onClick={() => {
          setHover(false);
          onClick();
          requestAnimationFrame(() => {
            const el = document.activeElement;
            if (el instanceof HTMLElement) {
              el.blur();
            }
          });
        }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setHover(false);
          setIsMenuOpen(true);
          if (!allowDragAndDrop(item, appointmentFilters.tableView)) {
            updateToast({
              id: item.id,
              title: t("prompt.copy_not_possible_title"),
              message: t("prompt.copy_not_possible_message"),
            });
          }
        }}
        aria-describedby={shouldShowTooltip ? tooltipId : undefined}
        onFocus={(e) => {
          // Only show the tooltip for keyboard focus, not for focus that is
          // programmatically restored after a modal closes.
          if (e.currentTarget.matches(":focus-visible")) {
            setHover(true);
          }
        }}
        onBlur={() => setHover(false)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <DragContainer horizontal={true} />
        {children}
        {isResizeEnabled && (
          <ResizeContainer
            horizontal={!verticalMode}
            ref={resizeSetNodeRef}
            {...resizeListeners}
          />
        )}
      </AppointmentBlockContainer>

      {shouldShowTooltip
        ? createPortal(
            <TooltipContainer
              id={tooltipId}
              role="tooltip"
              ref={setTooltipRef}
              top={coords.top}
              left={coords.left}
            >
              <TooltipContent item={item} />
            </TooltipContainer>,
            document.querySelector("#tooltip-portal")!
          )
        : null}
    </>
  );
};
